import * as React from 'react'
import { ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import { Button, makeStyles, mergeClasses, Popover, PopoverSurface, PopoverTrigger, Text, tokens } from '@fluentui/react-components'
import { DismissRegular, GiftOpenRegular, OpenRegular } from '@fluentui/react-icons'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Store } from '../../../reducers'
import { AppAction } from '../../../config/appConfig'
import { IAppState } from '../../../reducers/appReducer'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import PersonAvatar from '../../../components/PersonAvatar'
import IconValue from '../IconValueV9'
import { HammockIcon } from '../../../components/Icon/HammockIcon'
import AuthorizationContext from '../../../context/AuthorizationContext'
import { useApplicationStyles } from '../../../pages/App'

export type Build = {
    name: string
    version: string
    timestamp: bigint
    author: string
}

const useStyles = makeStyles({
    surface: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalS,
    },
    actions: {
        display: 'flex',
        columnGap: tokens.spacingHorizontalS,
        justifyContent: 'space-between',
    },
})

/**
 * v9 replacement for the legacy (Fluent-v8 TeachingBubble) Version widget in the footer. The version button in the
 * footer opens a Popover announcing the current release with a link to the release notes, following the same
 * pattern as the neighbouring Support widget.
 */
export default function Version(): ReactElement {
    const styles = useStyles()
    const appStyles = useApplicationStyles()
    const { t: translate } = useTranslation()
    const dispatch = useDispatch()
    const { host: currentHost, authenticated } = useContext(AuthorizationContext)
    const { viewport, isViewport } = useViewport()

    const version = (process.env.BUILD as unknown as Build).version
    const app = useSelector<Store, IAppState>(state => state.app)

    const [open, setOpen] = useState(false)

    const smallMenu = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    useEffect(() => {
        if (!app.version || app.version !== version) {
            // Log the app out so that we know we sure that the app state is renewed.
            dispatch({ type: AppAction.SetVersion, payload: version })
        }
    }, [])

    const openReleaseNotes = (): void => {
        setOpen(false)
        window.open(process.env.RELEASE_NOTES_URL, '_blank')
    }

    return (
        <>
            {smallMenu && (
                <PersonAvatar name={`${currentHost?.Name ?? ''} v${version}`} className={appStyles.alignSelf} color={'colorful'} size={24} />
            )}

            <Popover open={authenticated && open} onOpenChange={(_event, data) => setOpen(data.open)} positioning={'above-end'} withArrow trapFocus>
                <PopoverTrigger disableButtonEnhancement>
                    <Button
                        icon={<HammockIcon />}
                        appearance={'subtle'}
                        iconPosition={'after'}
                        aria-label={`${currentHost?.Name ?? ''} v${version}`}
                        className={appStyles.thinTextButton}
                    >
                        {!smallMenu && `${currentHost?.Name ?? ''} v${version}`}
                    </Button>
                </PopoverTrigger>

                <PopoverSurface className={styles.surface}>
                    <div className={mergeClasses(styles.actions, appStyles.alignItems)}>
                        <Text weight={'semibold'}>{translate('release.title')}</Text>

                        <Button
                            appearance={'subtle'}
                            icon={<DismissRegular />}
                            aria-label={translate('action.close')}
                            onClick={() => setOpen(false)}
                        />
                    </div>

                    {/* In a row, IconValue's alignSelf lines the icon up with the text instead of centring the line. */}
                    <div className={appStyles.flexRow}>
                        <IconValue icon={<GiftOpenRegular />}>{translate('release.version', { version: version })}</IconValue>
                    </div>
                    <Text>{translate('release.view')}</Text>

                    <div className={styles.actions}>
                        <Button icon={<OpenRegular />} appearance={'primary'} onClick={openReleaseNotes}>
                            {translate('release.notes')}
                        </Button>
                    </div>
                </PopoverSurface>
            </Popover>
        </>
    )
}
